import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/environment';
import * as vpsFirewallService from '../../services/vpsFirewallService';
import type { VPSFirewallRule, CreateFirewallRuleData, UpdateFirewallRuleData, FirewallStatus } from '../../services/vpsFirewallService';

interface VPSInstance {
  id: string;
  name: string;
  hostname: string;
  ipv6_address: string | null;
  status: string;
  customer_email: string;
}

const VPSFirewallManagement: React.FC = () => {
  const [vpsInstances, setVpsInstances] = useState<VPSInstance[]>([]);
  const [selectedVPSId, setSelectedVPSId] = useState<number | null>(null);
  const [rules, setRules] = useState<VPSFirewallRule[]>([]);
  const [firewallStatus, setFirewallStatus] = useState<FirewallStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingVPS, setLoadingVPS] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRule, setEditingRule] = useState<VPSFirewallRule | null>(null);
  const [applyingRules, setApplyingRules] = useState(false);

  const [formData, setFormData] = useState<CreateFirewallRuleData>({
    port: '',
    protocol: 'tcp',
    direction: 'in',
    action: 'allow',
    source_ip: '',
    description: '',
    is_active: true,
    priority: 100
  });

  useEffect(() => {
    fetchVPSInstances();
  }, []);

  useEffect(() => {
    if (selectedVPSId) {
      fetchRules();
      fetchStatus();
    }
  }, [selectedVPSId]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const fetchVPSInstances = async () => {
    try {
      setLoadingVPS(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/vps/instances'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success && data.instances && Array.isArray(data.instances)) {
        setVpsInstances(data.instances);
        // Auto-select first VPS if available
        if (data.instances.length > 0 && !selectedVPSId) {
          setSelectedVPSId(parseInt(data.instances[0].id));
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch VPS instances:', err);
    } finally {
      setLoadingVPS(false);
    }
  };

  const fetchRules = async () => {
    if (!selectedVPSId) return;

    try {
      setLoading(true);
      const data = await vpsFirewallService.getFirewallRules(selectedVPSId);
      setRules(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch firewall rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    if (!selectedVPSId) return;

    try {
      const status = await vpsFirewallService.getFirewallStatus(selectedVPSId);
      setFirewallStatus(status);
    } catch (err: any) {
      console.error('Failed to fetch firewall status:', err);
    }
  };

  const handleToggleRule = async (ruleId: number) => {
    if (!selectedVPSId) return;

    try {
      await vpsFirewallService.toggleFirewallRule(selectedVPSId, ruleId);
      setSuccess('Rule toggled successfully');
      fetchRules();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle rule');
    }
  };

  const handleApplyRules = async () => {
    if (!selectedVPSId) return;

    if (!window.confirm('Are you sure you want to apply all active firewall rules to the VPS? This will SSH into the VPS and configure UFW.')) {
      return;
    }

    try {
      setApplyingRules(true);
      const result = await vpsFirewallService.applyFirewallRules(selectedVPSId);
      setSuccess(`${result.message} - Applied ${result.applied_count} rules`);
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply firewall rules');
    } finally {
      setApplyingRules(false);
    }
  };

  const handleEnableFirewall = async () => {
    if (!selectedVPSId) return;

    try {
      const result = await vpsFirewallService.enableFirewall(selectedVPSId);
      setSuccess(result.message);
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to enable firewall');
    }
  };

  const handleDisableFirewall = async () => {
    if (!selectedVPSId) return;

    if (!window.confirm('Are you sure you want to disable the firewall? This will expose the VPS to all incoming connections.')) {
      return;
    }

    try {
      const result = await vpsFirewallService.disableFirewall(selectedVPSId);
      setSuccess(result.message);
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable firewall');
    }
  };

  const handleResetFirewall = async () => {
    if (!selectedVPSId) return;

    if (!window.confirm('Are you sure you want to reset the firewall? This will delete ALL firewall rules for this VPS.')) {
      return;
    }

    try {
      const result = await vpsFirewallService.resetFirewall(selectedVPSId);
      setSuccess(result.message);
      fetchRules();
      fetchStatus();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset firewall');
    }
  };

  const handleSetupDefaultRules = async () => {
    if (!selectedVPSId) return;

    try {
      const result = await vpsFirewallService.setupDefaultFirewall(selectedVPSId);
      setSuccess(`${result.message} - Created ${result.rules_created} default rules (SSH, HTTP, HTTPS)`);
      fetchRules();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to setup default rules');
    }
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVPSId) return;

    try {
      await vpsFirewallService.createFirewallRule(selectedVPSId, formData);
      setSuccess('Firewall rule created successfully');
      setShowAddModal(false);
      resetForm();
      fetchRules();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create firewall rule');
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVPSId || !editingRule) return;

    try {
      const updateData: UpdateFirewallRuleData = { ...formData };
      await vpsFirewallService.updateFirewallRule(selectedVPSId, editingRule.id, updateData);
      setSuccess('Firewall rule updated successfully');
      setShowEditModal(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update firewall rule');
    }
  };

  const handleDelete = async (ruleId: number, description: string) => {
    if (!selectedVPSId) return;

    if (!window.confirm(`Are you sure you want to delete this rule: "${description}"?`)) {
      return;
    }

    try {
      await vpsFirewallService.deleteFirewallRule(selectedVPSId, ruleId);
      setSuccess('Firewall rule deleted successfully');
      fetchRules();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete firewall rule');
    }
  };

  const openEditModal = (rule: VPSFirewallRule) => {
    setEditingRule(rule);
    setFormData({
      port: rule.port,
      protocol: rule.protocol,
      direction: rule.direction,
      action: rule.action,
      source_ip: rule.source_ip || '',
      description: rule.description || '',
      is_active: rule.is_active,
      priority: rule.priority
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      port: '',
      protocol: 'tcp',
      direction: 'in',
      action: 'allow',
      source_ip: '',
      description: '',
      is_active: true,
      priority: 100
    });
  };

  const selectedVPS = vpsInstances.find(vps => vps.id === selectedVPSId?.toString());

  const FormModal = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-t-lg">
          <h2 className="text-2xl font-bold">
            {isEdit ? '📝 Edit Firewall Rule' : '➕ Add New Firewall Rule'}
          </h2>
        </div>

        <form onSubmit={isEdit ? handleSubmitEdit : handleSubmitAdd} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Port/Range *</label>
              <input
                type="text"
                required
                value={formData.port}
                onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., 22 or 8000-9000"
              />
              <p className="text-xs text-gray-500 mt-1">Single port (22) or range (8000-9000)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Protocol *</label>
              <select
                value={formData.protocol}
                onChange={(e) => setFormData({ ...formData, protocol: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
                <option value="both">Both (TCP & UDP)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Direction *</label>
              <select
                value={formData.direction}
                onChange={(e) => setFormData({ ...formData, direction: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="in">Incoming (IN)</option>
                <option value="out">Outgoing (OUT)</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action *</label>
              <select
                value={formData.action}
                onChange={(e) => setFormData({ ...formData, action: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="allow">Allow</option>
                <option value="deny">Deny</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source IP (Optional)</label>
              <input
                type="text"
                value={formData.source_ip}
                onChange={(e) => setFormData({ ...formData, source_ip: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="e.g., 192.168.1.0/24"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank for any source</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
              <input
                type="number"
                required
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="100"
              />
              <p className="text-xs text-gray-500 mt-1">Lower number = higher priority</p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                rows={2}
                placeholder="e.g., Allow SSH access from office"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Active (will be applied to VPS)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                isEdit ? setShowEditModal(false) : setShowAddModal(false);
                resetForm();
                setEditingRule(null);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              {isEdit ? 'Update Rule' : 'Add Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="text-red-700 font-medium">⚠️ {error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <p className="text-green-700 font-medium">✅ {success}</p>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center text-white">
          <div>
            <h2 className="text-3xl font-bold mb-1">🔒 VPS Firewall Management</h2>
            <p className="text-orange-100">Manage UFW firewall rules for each VPS instance via SSH</p>
          </div>
        </div>
      </div>

      {/* VPS Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select VPS Instance</label>
        {loadingVPS ? (
          <p className="text-gray-500">Loading VPS instances...</p>
        ) : (
          <select
            value={selectedVPSId || ''}
            onChange={(e) => setSelectedVPSId(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-lg"
          >
            <option value="">-- Select a VPS --</option>
            {vpsInstances.map((vps) => (
              <option key={vps.id} value={vps.id}>
                {vps.name} - {vps.hostname} ({vps.ipv6_address}) - {vps.customer_email} - Status: {vps.status}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedVPSId && (
        <>
          {/* Firewall Status Card */}
          {firewallStatus && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Firewall Status for {selectedVPS?.name}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className={`text-2xl font-bold ${firewallStatus.enabled ? 'text-green-600' : 'text-red-600'}`}>
                        {firewallStatus.enabled ? '🟢 Enabled' : '🔴 Disabled'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Active Rules</p>
                      <p className="text-2xl font-bold text-blue-600">{firewallStatus.rules_count}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Last Applied</p>
                      <p className="text-sm text-gray-900">{firewallStatus.last_applied || 'Never'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {firewallStatus.enabled ? (
                    <button
                      onClick={handleDisableFirewall}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      🔓 Disable Firewall
                    </button>
                  ) : (
                    <button
                      onClick={handleEnableFirewall}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      🔒 Enable Firewall
                    </button>
                  )}
                  <button
                    onClick={handleApplyRules}
                    disabled={applyingRules}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:bg-gray-400"
                  >
                    {applyingRules ? '⏳ Applying...' : '🚀 Apply All Active Rules'}
                  </button>
                  <button
                    onClick={handleSetupDefaultRules}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    ⚙️ Setup Defaults
                  </button>
                  <button
                    onClick={handleResetFirewall}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
                  >
                    🔄 Reset Firewall
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rules Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Firewall Rules</h3>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
              >
                ➕ Add Rule
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                <p className="mt-4 text-gray-600">Loading rules...</p>
              </div>
            ) : rules.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-xl mb-2">📭 No firewall rules configured yet</p>
                <p className="text-sm">Click "Add Rule" or "Setup Defaults" to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Port</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Protocol</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Direction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source IP</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rules.map((rule) => (
                      <tr key={rule.id} className={`hover:bg-gray-50 ${!rule.is_active ? 'opacity-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rule.port}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">{rule.protocol}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">{rule.direction}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            rule.action === 'allow' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {rule.action.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rule.source_ip || 'Any'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{rule.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{rule.priority}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleRule(rule.id)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {rule.is_active ? '✓ Active' : '✗ Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          <button
                            onClick={() => openEditModal(rule)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(rule.id, rule.description || `Port ${rule.port}`)}
                            className="text-red-600 hover:text-red-800 font-medium"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      {showAddModal && <FormModal />}
      {showEditModal && <FormModal isEdit />}
    </div>
  );
};

export default VPSFirewallManagement;
